import { useState, useEffect } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useGlobalContext } from "../context/GlobalContext";
import useAuth from "../utils/AuthWrapper";
import { isDebugMode } from "../services/debugService";
import { useAudio } from "../context/AudioContext";
import {
  Switch,
  Dialog,
  DialogBackdrop,
  DialogPanel,
  TransitionChild,
} from "@headlessui/react";
import { ThreeBarsIcon } from "../assets/icons/index.js";
import {
  Button,
  BorderBox,
  GradientLine,
  DebugPanel,
  GlobalMusicBar,
  Tooltip,
} from "../components";
import MediaStatusIndicator from "../components/MediaStatusIndicator.jsx";
import {
  HomeIcon,
  FlowerIcon,
  MusicIcon,
  SunIcon,
  MoonIcon,
  QuestionIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  UserGroupIcon,
  FileLinesIcon,
  BookIcon,
  BooksIcon,
  PenIcon,
} from "../assets/icons";

const navigation = [
  {
    name: "",
    href: "/",
    icon: HomeIcon,
    label: "Home",
  },
  {
    name: "certificate",
    href: "",
    icon: FileLinesIcon,
    label: "Lesson",
  },
  {
    name: "current-degree",
    href: "",
    icon: BookIcon,
    label: "Degree",
  },
  {
    name: "degrees",
    href: "degrees",
    icon: BooksIcon,
    label: "Review",
  },
  {
    name: "journal",
    href: "journal",
    icon: PenIcon,
    label: "Journal",
  },
  {
    name: "music-player",
    href: "music-player",
    icon: MusicIcon,
    label: "Music",
  },
  {
    name: "explore",
    href: "explore",
    icon: MagnifyingGlassIcon,
    label: "Explore",
  },
  {
    name: "community",
    href: "community",
    icon: UserGroupIcon,
    label: "Community",
  },
];

function isActiveTab(activeTab, item) {
  if (activeTab === "lessons" && item.name === "certificate") {
    return true;
  }

  if (activeTab === "degree" && item.name === "current-degree") {
    return true;
  }

  if (activeTab === "post" && item.name === "community") {
    return true;
  }

  return activeTab === item.name;
}

export default function AppLayout() {
  const { darkMode, setDarkMode } = useGlobalContext();
  const { user } = useGlobalContext();
  const { fetchUser } = useAuth();
  // Grab music state for padding calculation (showMusicBar mirrors the
  // visibility predicate inside GlobalMusicBar).
  const { currentTrack, playerActive } = useAudio();
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = location.pathname.split("/")[1];
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // The mini-player is hidden on Home (has MusicWidget) and Music Player
  // (has full controls). Otherwise it shows once the user has engaged the
  // player and hasn't dismissed it.
  const path = location.pathname;
  const musicBarHiddenOnRoute = path === "/" || path === "/music-player";
  const showMusicBar =
    currentTrack && playerActive && !musicBarHiddenOnRoute;

  // 📐 ROS-203: mini-player is now a tiny pill at bottom-[75px] right — barely needs padding
  // Just enough so scrolled-to-bottom content peeks above the bar's 40px height + 75px offset
  const barPadding = showMusicBar ? 40 : 24;

  // Fetch user data on initial load and location changes
  useEffect(() => {
    const handleFetchUser = async () => {
      try {
        await fetchUser();
      } catch (error) {
        if (error.status === 401 || error.message === "No session found") {
          // In debug mode, don't redirect to unauthorized - let user use debug panel
          if (!isDebugMode) {
            navigate("/unauthorized", { replace: true }); // Navigate to unauthorized page
          }
        }
      }
    };

    handleFetchUser();
  }, [location.pathname, fetchUser, navigate]);

  const toggleDarkMode = (checked) => {
    if (checked) {
      localStorage.theme = "dark";
      document.documentElement.classList.add("dark");
    } else {
      localStorage.theme = "light";
      document.documentElement.classList.remove("dark");
    }
    setDarkMode(checked);
  };

  return (
    <>
      <div
        id="tracking"
        data-user-id={user?.id}
        className="shrink-0 min-h-full lg:h-full bg-lz-concrete dark:bg-lz-ink relative"
      >
        <Dialog
          open={sidebarOpen}
          onClose={setSidebarOpen}
          className="relative z-50 lg:hidden"
        >
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-lz-concrete dark:bg-lz-ink transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
          />

          <div className="fixed inset-0 flex">
            <DialogPanel
              transition
              className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
            >
              <TransitionChild>
                <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                  <button
                    type="button"
                    onClick={() => setSidebarOpen(false)}
                    className="-m-2.5 p-2.5"
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon
                      aria-hidden="true"
                      className="size-6 fill-lz-ink-mid dark:fill-lz-concrete"
                    />
                  </button>
                </div>
              </TransitionChild>
              {/* Mobile Sidebar */}
              <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-lz-paper dark:bg-lz-charcoal px-6 pb-4 ring-1 ring-white/10">
                <div className="flex h-16 shrink-0 items-center">
                  <FlowerIcon className="fill-lz-ink-mid dark:fill-lz-concrete size-6" />
                  <h1 className="ml-2 text-black dark:text-lz-concrete">
                    Lezioni
                  </h1>
                  <div className="flex items-center ml-auto">
                    <SunIcon className="size-4 mr-2 fill-lz-ink-mid dark:fill-lz-concrete" />
                    <Switch
                      checked={darkMode}
                      onChange={toggleDarkMode}
                      className="group relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-full"
                    >
                      <span className="sr-only">Toggle dark mode</span>
                      <span
                        aria-hidden="true"
                        className="pointer-events-none absolute h-full w-full rounded-md bg-lz-concrete dark:bg-lz-ink"
                      />
                      <span
                        aria-hidden="true"
                        className="pointer-events-none absolute mx-auto h-3 w-9 rounded-full bg-lz-ink dark:bg-lz-concrete transition-colors duration-200 ease-in-out group-data-[checked]:bg-lz-ink border dark:border-lz-concrete border-lz-ink"
                      />
                      <span
                        aria-hidden="true"
                        className="pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full border dark:border-lz-concrete border-lz-ink bg-lz-concrete shadow ring-0 transition-transform duration-200 ease-in-out group-data-[checked]:translate-x-5"
                      />
                    </Switch>
                    <MoonIcon className="size-4 ml-2 fill-lz-ink-mid dark:fill-lz-concrete" />
                  </div>
                </div>
                <nav className="flex flex-1 flex-col gap-y-4">
                  {navigation.map((item) => {
                    const getHref = () => {
                      if (item.name === "certificate") {
                        return `/lessons/${user?.current_lesson_slug}`;
                      }
                      if (item.name === "current-degree") {
                        return `/degree/${
                          user?.current_degree_slug || "neophyte"
                        }`;
                      }
                      return item.href;
                    };

                    return (
                      <Link
                        key={item.name || item.href}
                        to={getHref()}
                        className="w-full"
                        onClick={() => setSidebarOpen(false)}
                      >
                        <Button icon={<item.icon />} className="w-full">
                          {item.label}
                        </Button>
                      </Link>
                    );
                  })}
                  <Link
                    to="help"
                    className="w-full"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Button icon={<QuestionIcon />} className="w-full">
                      Help
                    </Button>
                  </Link>
                </nav>
              </div>
            </DialogPanel>
          </div>
        </Dialog>
        {/* Desktop Sidebar */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-24 lg:flex-col">
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <div className="flex grow flex-col gap-y-5 ml-4 my-4 bg-lz-concrete dark:bg-lz-ink">
            <BorderBox gradient>
              <div className="flex flex-col h-full">
                <nav className="flex flex-1 flex-col">
                  <ul
                    role="list"
                    className="flex flex-1 flex-col items-center h-full"
                  >
                    <li className="-mt-1">
                      <ul
                        role="list"
                        className="-mx-2 space-y-4 flex flex-col items-center"
                      >
                        {navigation.map((item) => (
                          <li key={item.name}>
                            <Link
                              to={
                                item.name === "certificate"
                                  ? `/lessons/${user?.current_lesson_slug}`
                                  : item.name === "current-degree"
                                    ? `/degree/${
                                        user?.current_degree_slug || "neophyte"
                                      }`
                                    : item.href
                              }
                              className={`group flex flex-col items-center gap-y-1 rounded-md p-2 text-sm font-semibold leading-6 border w-16 ${
                                isActiveTab(activeTab, item)
                                  ? "bg-lz-concrete dark:bg-lz-ink-mid border-lz-paper-border dark:border-lz-charcoal-border"
                                  : "hover:bg-lz-concrete dark:hover:bg-lz-ink-mid border-lz-paper dark:border-lz-charcoal"
                              }`}
                            >
                              <item.icon
                                aria-hidden="true"
                                className={`h-6 w-6 shrink-0 ${
                                  isActiveTab(activeTab, item)
                                    ? "fill-lz-ink-mid dark:fill-lz-concrete"
                                    : "fill-lz-ink-mid dark:fill-lz-concrete"
                                }`}
                              />
                              <span
                                className={`text-xs font-semibold leading-6 ${
                                  isActiveTab(activeTab, item)
                                    ? "text-lz-ink-mid dark:text-lz-concrete"
                                    : "text-lz-ink-mid dark:text-lz-concrete"
                                }`}
                              >
                                {item.label}
                              </span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </li>
                    <li className="mt-auto flex flex-col items-center">
                      <GradientLine className="mb-4 w-full" />
                      <Link
                        to="help"
                        data-tooltip-id="nav-help-tooltip"
                        data-tooltip-content="Open help & tutorials"
                        className={`group flex flex-col items-center gap-y-1 rounded-md p-2 text-sm font-semibold leading-6 border w-16 ${
                          isActiveTab(activeTab, { name: "help" })
                            ? "bg-lz-concrete dark:bg-lz-ink-mid border-lz-paper-border dark:border-lz-charcoal-border"
                            : "hover:bg-lz-concrete dark:hover:bg-lz-ink-mid border-lz-paper dark:border-lz-charcoal"
                        }`}
                      >
                        <QuestionIcon
                          aria-hidden="true"
                          className={`h-6 w-6 shrink-0 ${
                            isActiveTab(activeTab, { name: "help" })
                              ? "fill-lz-ink-mid dark:fill-lz-concrete"
                              : "fill-lz-ink-mid dark:fill-lz-concrete"
                          }`}
                        />
                        <span className="sr-only">Help</span>
                      </Link>
                    </li>
                  </ul>
                </nav>
              </div>
            </BorderBox>
          </div>
        </div>

        <div className="lg:pl-24 flex flex-col lg:h-full xxl:max-h-screen">
          <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 bg-lz-concrete dark:bg-lz-ink text-black dark:text-white px-4 sm:gap-x-6 sm:px-6 lg:px-8">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            >
              <span className="sr-only">Open sidebar</span>
              <ThreeBarsIcon
                aria-hidden="true"
                className="size-6 fill-lz-ink-mid dark:fill-lz-concrete"
              />
            </button>

            {/* Separator */}
            <div
              aria-hidden="true"
              className="h-6 w-px bg-gray-900/10 dark:bg-lz-concrete/10 lg:hidden"
            />

            {/* Header */}
            <div className="flex flex-1 gap-x-4 items-center lg:gap-x-6 relative">
              <div className="lg:flex items-center hidden">
                <FlowerIcon className="fill-lz-ink-mid dark:fill-lz-concrete size-6 mr-2" />
                <h1 className="text-lz-ink-mid dark:text-lz-concrete">
                  Welcome to Lezioni!
                </h1>
              </div>
              <div className="flex lg:hidden">
                <FlowerIcon className="fill-lz-ink-mid dark:fill-lz-concrete size-6" />
                <h1 className="ml-2">Lezioni</h1>
              </div>
              <div className="flex items-center absolute left-1/2 -translate-x-1/2">
                <SunIcon className="hidden lg:block size-4 mr-2 fill-lz-ink-mid dark:fill-lz-concrete" />
                <Switch
                  checked={darkMode}
                  onChange={toggleDarkMode}
                  className="group relative hidden lg:inline-flex h-5 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-full"
                >
                  <span className="sr-only">Toggle dark mode</span>
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute h-full w-full rounded-md bg-lz-concrete dark:bg-lz-ink"
                  />
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute mx-auto h-3 w-9 rounded-full bg-lz-ink dark:bg-lz-concrete transition-colors duration-200 ease-in-out group-data-[checked]:bg-lz-ink border dark:border-lz-concrete border-lz-ink"
                  />
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full border dark:border-lz-concrete border-lz-ink bg-lz-concrete shadow ring-0 transition-transform duration-200 ease-in-out group-data-[checked]:translate-x-5"
                  />
                </Switch>
                <MoonIcon className="hidden lg:block size-4 ml-2 fill-lz-ink-mid dark:fill-lz-concrete" />
              </div>
              <div className="flex items-center gap-x-4 lg:gap-x-6 ml-auto">
                {/* <NotificationMenu user={user} /> */}
                <p className="hidden lg:block dark:text-lz-concrete">
                  {user?.name}
                </p>
              </div>
            </div>
          </div>

          <main
            className="flex flex-1 flex-col px-6"
            style={{
              paddingBottom: `${barPadding}px`,
              transition: "padding-bottom 0.3s ease",
            }}
          >
            <Outlet context={{ showMusicBar }} />
          </main>
        </div>

        {/* Global Music Bar - shows on all pages when music is playing */}
        <GlobalMusicBar />

        {/* Debug Panel - only shows in development */}
        <DebugPanel />

        {/* Media Status Indicator - only shows in development */}
        <MediaStatusIndicator />

        <Tooltip id="nav-help-tooltip" place="right" ariaLabel="Open help and tutorials" />
      </div>
    </>
  );
}
